import {
  Component, Input, ChangeDetectorRef,
  Optional, OnInit, ViewEncapsulation
} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {QuestionnaireEditorService} from '../../../services/questionnaire-editor.service';
import {QuestionnaireConditionService} from '../../../services/questionnaire-condition.service';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';


// hack to inject decorator declarations
export interface QuestionTypeLineChartComponent extends Translatable {}

@Component({
  selector: 'app-qa-question-line-chart',
  templateUrl: './question-type-line-chart.component.html',
  styleUrls: ['./question-type-line-chart.component.css'],
  encapsulation: ViewEncapsulation.None
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionTypeLineChartComponent implements OnInit {
  @Input() question: Questionnaire.Container;

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   */
  public locale = 'de_DE';

  /**
   * Default option for select2 component
   */
  public select2Opts = {width: '200px', placeholder: '...'};

  /**
   * List of available numeric variables
   */
  public variables: string[] = [];

  /**
   * Alignment options for the chart
   */
  public alignmentOptions = [
    {value: 'left', label: 'CHART_ALIGNMENT_LEFT'},
    {value: 'center', label: 'CHART_ALIGNMENT_CENTER'},
    {value: 'right', label: 'CHART_ALIGNMENT_RIGHT'}
  ];

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private questionnaireEditor: QuestionnaireEditorService,
    private conditions: QuestionnaireConditionService,
    private ref: ChangeDetectorRef,
  ) {
  }

  /**
   * Initializes numeric variables list
   */
  public ngOnInit() {
    const variables = [];
    const select = this.conditions.getSelectData(false);

    for (const variable of select.variables) {
      const container =
        this.conditions.getContainerForVariable(variable);
      if (container && container.format === 'numeric') {
        variables.push(variable);
      }
    }

    this.variables = variables;
  }

  /**
   * Returns the chart data object from question options
   */
  get chartData(): any {
    return this.question.options.lineChart;
  }

  /**
   * Returns the list of line options with id and title for the dropdown
   */
  get lineOptions(): {id: string; title: string}[] {
    if (!this.chartData.lines) {
      return [];
    }

    return this.chartData.lines
      .filter(line => line.id)
      .map(line => ({
        id: line.id,
        title: line.title[this.locale] || line.id
      }));
  }

  /**
   * Changes position of an entry in the entries list
   */
  public onPositionChange(
    currentPosition: number,
    newPosition: number
  ) {
    const entries = this.chartData.entries;

    if (
      currentPosition >= entries.length
      || newPosition < 0
    ) {
      return;
    }

    entries.splice(
      newPosition, 0,
      entries.splice(currentPosition, 1)[0]
    );
    this.triggerChangeDetection();
  }

  /**
   * Adds a new entry to the chart
   */
  public onAddEntry() {
    const defaultLineId = this.chartData.lines
      && this.chartData.lines.length > 0
      ? this.chartData.lines[0].id || ''
      : '';

    this.chartData.entries.push({
      label: {[this.locale]: ''},
      variable: '',
      color: '#4285f4',
      lineId: defaultLineId,
    });

    this.triggerChangeDetection();
  }

  /**
   * Deletes an entry from the chart
   */
  public onDeleteEntry(position: number) {
    this.questionnaireEditor.confirmDelete(() => {
      this.chartData.entries.splice(position, 1);
      this.triggerChangeDetection();
    });
  }

  /**
   * Adds a new line definition
   */
  public onAddLine() {
    if (!this.chartData.lines) {
      this.chartData.lines = [];
    }

    const existingIds = this.chartData.lines
      .map(l => l.id || '')
      .filter(id => id.startsWith('line-'))
      .map(id => parseInt(id.replace('line-', ''), 10))
      .filter(n => !isNaN(n));
    const nextNum = existingIds.length > 0
      ? Math.max(...existingIds) + 1 : 1;

    this.chartData.lines.push({
      id: `line-${nextNum}`,
      title: {[this.locale]: ''},
      color: '#4285f4',
    });

    this.triggerChangeDetection();
  }

  /**
   * Deletes a line definition
   */
  public onDeleteLine(position: number) {
    this.questionnaireEditor.confirmDelete(() => {
      this.chartData.lines.splice(position, 1);
      this.triggerChangeDetection();
    });
  }

  /**
   * Tracks line options by their stable id for Angular change detection
   */
  public trackByLineId(index: number, option: {id: string}): string {
    return option.id;
  }

  /**
   * Triggers change detection
   */
  public triggerChangeDetection() {
    this.ref.detectChanges();
    this.ref.markForCheck();
  }
}
